import { Button, Empty, Space } from 'antd';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import 'highcharts/modules/export-data';
import 'highcharts/modules/exporting';
import 'highcharts/modules/full-screen';
import 'highcharts/modules/offline-exporting';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import allActions from '../../../../../actions';
import { NUMBER_OF_RECORDS_TO_FETCH } from '../../../../../utils/GlobalConstants';
import { widgetViewVals } from '../../../../Config/adminSettings/widgetSetting/WidgetViewConstants';
import styles from '../styles.module.scss';
import { filterWidgetData, getDashboardFilters, hasDataAfterFilter } from '../utils/WidgetFunctions';

function HorizontalGraphWidget({ theme, inventoryData }) {
  const selectedPlants = useSelector((state) => state.commonState.selectedPlants);
  const selectedCCs = useSelector((state) => state.commonState.selectedCompanyCodes);
  const userSettings = useSelector((state) => state.commonState.userSettings);
  const dashboardFilter = useSelector((state) => state.commonState.localDashboardFilters);
  const dragStatus = useSelector((state) => state.dashboardState.dragStatus);
  const widgetViews = useSelector((state) => state.commonState.widgetViews);
  const gridData = useSelector((state) => state.dashboardState.gridData);
  const [data, setData] = useState([]);
  const [containerNode, setContainerNode] = useState(null);
  const [chartHeight, setChartHeight] = useState(300);
  const [resolvedColors, setResolvedColors] = useState(null);
  const [animation, setAnimation] = useState(false);
  const containerRef = React.useCallback((node) => {
    setContainerNode(node);
  }, []);

  const statusTypes = {
    openWithInsufficient: 'Open reservations with insufficient coverage',
    openWithSufficient: 'Open reservations with sufficient coverage',
    openWithIncoming: 'Open reservations with incoming coverage',
    replenishment: 'Replenishment only',
  };

  const statusLabels = {
    [statusTypes.openWithIncoming]: ['Open reservations with', 'incoming coverage'],
    [statusTypes.openWithInsufficient]: ['Open reservations with', 'insufficient coverage'],
    [statusTypes.openWithSufficient]: ['Open reservations with', 'sufficient coverage'],
    [statusTypes.replenishment]: ['Replenishment', 'only'],
  };

  // const tickFormat = (v) => {
  //   const labels = {
  //     [statusTypes.openWithIncoming]: ['Open reservations with', 'incoming coverage'],
  //     [statusTypes.openWithInsufficient]: ['Open reservations with', 'insufficient coverage'],
  //     [statusTypes.openWithSufficient]: ['Open reservations with', 'sufficient coverage'],
  //     [statusTypes.replenishment]: ['Replenishment', 'only'],
  //   };
  //   const valueArray = labels[v];
  //   if (valueArray?.length === 2) {
  //     return (
  //       <tspan x="0" y="0">
  //         {' '}
  //         <tspan x="0" dy="0">{valueArray[0]}</tspan>
  //         {' '}
  //         <tspan x="0" dy="10">{valueArray[1]}</tspan>
  //         {' '}
  //       </tspan>
  //     );
  //   }
  //   return labels?.[v]?.join() || v;
  // };

  useEffect(() => {
    if (_.isEmpty(inventoryData)) {
      setData([]);
      return;
    }
    if (inventoryData) {
      const filteredData = filterWidgetData(inventoryData, dashboardFilter);
      const formattedData = {};
      Object.values(statusTypes).forEach((status) => {
        const count = filteredData?.filter((x) => x?.PLANNED_ORDER_STATUS === status)
          ?.reduce(((prev, curr) => prev + (curr?.COUNT ?? 0)), 0);
        formattedData[status] = count ?? 0;
      });
      const result = Object.keys(formattedData).map((x) => ({
        status: x,
        count: formattedData[x],
      }))?.sort((a, b) => (b?.count ?? 0) - (a?.count ?? 0));
      setData(result);
    }
  }, [inventoryData, dashboardFilter]);

  useEffect(() => {
    if (containerNode) {
      const computedStyle = getComputedStyle(containerNode);
      const newColors = {
        [statusTypes.openWithInsufficient]: computedStyle.getPropertyValue('--open-with-insufficient').trim(),
        [statusTypes.openWithSufficient]: computedStyle.getPropertyValue('--open-with-sufficient').trim(),
        [statusTypes.openWithIncoming]: computedStyle.getPropertyValue('--open-with-incoming').trim(),
        [statusTypes.replenishment]: computedStyle.getPropertyValue('--replenishment').trim(),
        neutral100: computedStyle.getPropertyValue('--neutral--100').trim(),
        neutral400: computedStyle.getPropertyValue('--neutral--400').trim(),
      };
      setResolvedColors(newColors);
    }
  }, [containerNode]);

  const dispatch = useDispatch();
  const history = useHistory();

  const getColor = (x) => {
    if (resolvedColors) {
      return resolvedColors[x.status] || '#888888';
    }
    switch (x.status) {
      case (statusTypes.openWithInsufficient):
        return 'var(--open-with-insufficient)';
      case (statusTypes.openWithIncoming):
        return 'var(--open-with-incoming)';
      case (statusTypes.openWithSufficient):
        return 'var(--open-with-sufficient)';
      case (statusTypes.replenishment):
        return 'var(--replenishment)';
      default: return '#888888';
    }
  };

  const handleClick = (e, type) => {
    if (dragStatus) return;
    const indexValue = e.name;
    let viewID = null;
    if (type) {
      viewID = widgetViews?.find(
        (x) => x.WidgetID === 'plannedOrders' && x.GroupID === widgetViewVals.all,
      )?.ViewID;
    }
    viewID = widgetViews?.find(
      (x) => x.WidgetID === 'plannedOrders' && (x.GroupID === indexValue || x.GroupID === widgetViewVals.all),
    )?.ViewID;
    if (viewID) {
      dispatch(allActions.CommonActions.selectWidgetView(viewID));
    } else {
      dispatch(allActions.CommonActions.selectWidgetView(userSettings?.DEFAULT_USER_VIEW));
    }
    let searchObject = {};

    if (type === 'readyForRelease') {
      searchObject = {
        ...searchObject,
        PLANNED_ORDER_ANALYSIS: [
          {
            ColumnName: 'PLANNED_ORDER_ANALYSIS',
            FilterOperator: 'EqualTo',
            FilterValue: ['Partly ready for release', 'Ready for release'],
          },
        ],
      };
    }

    if (type) {
      searchObject = {
        ...searchObject,
        PLANNED_ORDER_STATUS: [
          {
            ColumnName: 'PLANNED_ORDER_STATUS',
            FilterOperator: 'EqualTo',
            FilterValue: Object.values(statusTypes),
          },
        ],
      };
    } else {
      searchObject = {
        PLANNED_ORDER_STATUS: [
          {
            ColumnName: 'PLANNED_ORDER_STATUS',
            FilterOperator: 'EqualTo',
            FilterValue: [
              indexValue,
            ],
          },
        ],
      };
    }
    if (selectedPlants?.length > 0) {
      searchObject = {
        ...searchObject,
        PLANT_FACILITY_SAP_ID: [
          {
            ColumnName: 'PLANT_FACILITY_SAP_ID',
            FilterOperator: 'EqualTo',
            FilterValue:
              selectedPlants,
          },
        ],
      };
    } else if (selectedCCs?.length > 0) {
      searchObject = {
        ...searchObject,
        COMPANY_CODE: [
          {
            ColumnName: 'COMPANY_CODE',
            FilterOperator: 'EqualTo',
            FilterValue:
              selectedCCs,
          },
        ],
      };
    }
    searchObject = {
      ...searchObject,
      ...getDashboardFilters(dashboardFilter),
    };
    const materialViewState = {
      headerCells: [],
      searchFilters: searchObject,
      scrollPage: {
        currentNoOfRecords: 0,
        noOfRecordsToFetch: NUMBER_OF_RECORDS_TO_FETCH,
      },
    };
    if ((selectedPlants?.length > 0
      && !_.isEqual(selectedPlants?.sort(), userSettings?.DEFAULT_PLANTS?.sort()))
      || (selectedCCs?.length > 0
        && !_.isEqual(selectedCCs?.sort(), userSettings?.DEFAULT_COMPANY_CODES?.sort()))) {
      dispatch(allActions.CommonActions.setUseDefaultSettings(false));
    }
    dispatch(allActions.MaterialListActions.setLocalMaterialView(materialViewState));
    dispatch(allActions.MaterialListActions.setSelectedRowKeys([]));
    history.push('/material-list');
  };

  const chartComponentRef = useRef(null);

  useEffect(() => {
    if (!containerNode) return;
    const updateDimensions = () => {
      if (!document.fullscreenElement) {
        const width = containerNode.offsetWidth;
        if (!gridData) {
          setChartHeight((width * 0.33) + 10);
          return;
        }
        const totalHeight = gridData?.gridHeight;
        let finalHeight = totalHeight;
        if (gridData?.colCount === 3) {
          finalHeight = (totalHeight / 3) - 50;
        }
        setChartHeight(finalHeight);
      } else {
        setChartHeight(window.innerHeight);
      }
    };
    updateDimensions();
    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(containerNode);
    // eslint-disable-next-line consistent-return
    return () => {
      resizeObserver.disconnect();
    };
  }, [containerNode, gridData]);

  const options = {
    ...theme,
    exporting: {
      enabled: true,
      url: '',
      local: true,
      fallbackToExportServer: false,
      error: (_opt, err) => console.log(err),
      menuItemDefinitions: {
        viewData: {
          text: 'View data table'
        }
      },
      buttons: {
        contextButton: {
          menuItems: [
            'viewFullscreen',
            'printChart',
            'downloadPNG',
          ],
          symbol: 'menu',
          symbolX: 3,
          symbolFill: 'var(--neutral--100)',
          symbolStroke: 'var(--neutral--100)',
          symbolStrokeWidth: 1,
          theme: {
            fill: 'transparent',
            states: {
              hover: {
                fill: 'transparent'
              },
              select: {
                fill: 'transparent'
              }
            }
          },
          y: -18,
          x: 10,
          verticalAlign: 'top',
          align: 'right'
        }
      },
      chartOptions: {
        chart: {
          marginTop: 10
        }
      }
    },
    navigation: {
      buttonOptions: {
        align: 'right',
        verticalAlign: 'top',
        y: -50,
        x: 10,
        theme: {
          padding: 8
        }
      },
      menuStyle: {
        background: 'var(--neutral--800)',
        color: 'var(--neutral--100)'
      },
      menuItemStyle: {
        color: 'var(--neutral--100)',
        fontSize: '14px',
        padding: '0.5em 1em',
        transition: 'background 200ms',
      },
      menuItemHoverStyle: {
        background: 'var(--neutral--600)',
        color: 'var(--neutral--100)'
      }
    },
    chart: {
      ...theme.chart,
      type: 'bar',
      height: chartHeight,
      spacingTop: 10,
      marginTop: 10,
      spacing: [10, 10, 10, 10],
      animation: false,
      backgroundColor: 'var(--widget-background)',
      events: {
        load() {
          if (!animation) {
            setAnimation({
              duration: 1200,
              easing: 'easeOutBounce',
              start: {
                x: 0
              }
            });
          }
        },
      }
    },
    title: {
      text: undefined,
    },
    responsive: {
      rules: [{
        condition: {
          maxWidth: 500,
        },
        chartOptions: {
          legend: {
            align: 'center',
            verticalAlign: 'bottom',
            layout: 'horizontal',
          },
          yAxis: {
            labels: {
              align: 'left',
            },
            title: {
              text: null,
            },
          },
        },
      }],
    },
    xAxis: {
      ...theme.xAxis,
      categories: data?.map((x) => statusLabels[x.status]?.join(' ')),
      title: {
        text: null,
      },
      labels: {
        ...theme.xAxis?.labels,
        style: {
          ...theme.xAxis?.labels?.style,
          width: '150px',
          whiteSpace: 'normal',
          textOverflow: 'ellipsis',
          color: resolvedColors?.neutral400 || 'var(--neutral--400)',
          textOutline: 'none',
        },
        useHTML: true,
        formatter() {
          const { value } = this;
          return `<div style="width:150px; overflow:hidden; text-overflow:ellipsis; white-space:normal">${value}</div>`;
        },
      },
    },
    yAxis: {
      ...theme.yAxis,
      type: 'logarithmic',
      title: {
        ...theme.yAxis?.title,
        text: null,
      },
      labels: {
        ...theme.yAxis?.labels,
        formatter() {
          const test = this;
          return test.value <= 1 ? '0' : test.value;
        },
      },
      min: 1,
      max: data?.reduce((max, item) => Math.max(max, item.count), 0),
      gridLineWidth: 0,
      minorTickInterval: 0.1,
      minorGridLineWidth: 0,
      softMin: 0,
      startOnTick: false,
    },
    plotOptions: {
      bar: {
        groupPadding: 0.2,
        pointPadding: 0.1,
        borderRadius: '50%',
        dataLabels: {
          enabled: true,
          style: {
            color: resolvedColors?.neutral100 || 'var(--neutral--100)',
            fontWeight: 400,
            textOutline: 'none',
            textStroke: 'none',
            WebkitTextStroke: 'none',
          },
          formatter() {
            // eslint-disable-next-line react/no-this-in-sfc
            return this.point?.y;
          },
        },
        borderWidth: 0,
      },
      series: {
        point: {
          events: {
            click: (event) => {
              handleClick(event.point.options);
            }
          }
        },
        animation: false,
      }
    },
    colors: theme.colors,
    series: [{
      name: '',
      data: data.map((item) => ({
        name: item.status,
        y: Number(item.count || 0),
        color: getColor(item),
      })),
    }],
    credits: {
      enabled: false,
    },
    tooltip: {
      ...theme.tooltip,
      followPointer: true,
    },
  };

  return data.length > 0 && hasDataAfterFilter(data) ? (
    <>
      <div className={styles.widgetButtons}>
        <Button
          style={{
            padding: '0px 8px',
            fontSize: '12px',
            height: '26px',
          }}
          onClick={(e) => handleClick(e, 'all')}
        >
          All planned orders
        </Button>
        <Button
          style={{
            padding: '0px 8px',
            fontSize: '12px',
            height: '26px',
          }}
          onClick={(e) => handleClick(e, 'readyForRelease')}
        >
          Ready for release
        </Button>
      </div>
      <div ref={containerRef} className={styles.chartContainer}>
        <HighchartsReact
          highcharts={Highcharts}
          immutable
          options={{
            ...options,
            legend: {
              enabled: false,
            },
          }}
          ref={chartComponentRef}
        />
      </div>
    </>
  ) : (
    <Space
      direction="vertical"
      style={{
        width: '100%', height: '100%', justifyContent: 'center', alignItems: 'center',
      }}
    >
      <Empty description={_.isEmpty(dashboardFilter?.filterVals) ? 'No data' : 'No data after filtering'} />
    </Space>
  );
}

export default HorizontalGraphWidget;
HorizontalGraphWidget.defaultProps = {};

HorizontalGraphWidget.propTypes = {
  inventoryData: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
    PropTypes.objectOf(PropTypes.any)]).isRequired,
  theme: PropTypes.objectOf(PropTypes.any).isRequired,
};
